var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState } from 'react';
import { TreeBodyLayout } from "../layouts/TreeBodyLayout";
import { TreeView, NodeData } from "../TreeView";
import { useModel, useInteractor, Model } from '@veeam/core';
import { DataTreeInteractor } from "./DataTreeInteractor";
import { useContextMenu, buildCollectionToolbar } from "../smart";
import { useStateValue } from "../helpers";
export function DataTree(_a) {
    var model = _a.model, toolbar = _a.toolbar, _b = _a.config, config = _b === void 0 ? {} : _b, flags = _a.flags, fragment = _a.fragment, loading = _a.loading, disabled = _a.disabled, error = _a.error;
    var asyncNodesModel = useState(function () { return new Model({ loadingIds: [], warningMap: [] }); })[0];
    var interactor = useInteractor(function () {
        var _a;
        return new DataTreeInteractor(model, asyncNodesModel, {
            autoLoad: (_a = config.autoLoad) !== null && _a !== void 0 ? _a : true,
            onAsyncNodeLoad: config.onAsyncNodeLoad
        });
    }).interactor;
    var _c = useModel(model), value = _c.value, meta = _c.meta;
    var _d = useModel(interactor.asyncNodesModel).value, loadingIds = _d.loadingIds, warningMap = _d.warningMap;
    var getNodeData = useStateValue(NodeData.createHierarchy(value.items, model.getItemID, model.getItemChilds, config.asyncGetter), [value.items, model.getItemID, model.getItemChilds, config.asyncGetter]).getValue;
    var checkableConfig = config.checkableConfig, expandConfig = config.expandConfig, texts = config.texts, onToggleExpandNode = config.onToggleExpandNode, restConfig = __rest(config, ["checkableConfig", "expandConfig", "texts", "onToggleExpandNode"]);
    var internalLoading = loading || meta.loading;
    var selectedNodes = getNodeData()
        .reduce(function (acc, nodeData) { return acc.concat(nodeData.getDescendants()); }, [])
        .filter(function (desd) { return value.selection.includes(desd.id); });
    var _e = buildCollectionToolbar({
        config: toolbar,
        model: model,
        payloadGetter: function () { return ({
            selectedItems: selectedNodes,
            loading: internalLoading,
            flags: flags
        }); }
    }), toolbarElement = _e.element, contextMenuActions = _e.contextMenuActions;
    var contextMenu = useContextMenu({
        items: contextMenuActions.map(function (item) {
            return {
                id: item.id,
                title: item.title || '',
                icon: item.icon,
                decorators: item.decorators,
                disabled: item.disabled,
                hidden: item.hidden,
                handler: function () { var _a; return (_a = item.handler) === null || _a === void 0 ? void 0 : _a.call(item); }
            };
        }),
        payloadGetter: function () { return ({
            selectedItems: selectedNodes,
            loading: internalLoading,
            flags: flags
        }); }
    });
    var onSelectCallback = useCallback(function (selectedIds) {
        interactor.didSelectionChange(selectedIds);
    }, [interactor]);
    var onCheckCallback = useCallback(function (checked) {
        interactor.didCheckedChange(checked);
    }, [interactor]);
    var onExpandCallback = useCallback(function (expanded) {
        interactor.didExpandChange(expanded);
    }, [interactor]);
    var onToggleExpandNodeCallback = useCallback(function (node, isExpanded) {
        interactor.didToggleExpandNode(node, isExpanded);
        onToggleExpandNode === null || onToggleExpandNode === void 0 ? void 0 : onToggleExpandNode(node, isExpanded);
    }, [onToggleExpandNode, interactor]);
    var onConxtextMenuCallback = useCallback(function (node, nativeEvent) {
        if (!disabled && !internalLoading && contextMenuActions.length) {
            nativeEvent.preventDefault();
            contextMenu.show({
                offsetX: nativeEvent.pageX,
                offsetY: nativeEvent.pageY
            });
        }
    }, [disabled, internalLoading, contextMenuActions.length]);
    return (React.createElement(TreeBodyLayout, { toolbar: toolbarElement, fragment: React.createElement(React.Fragment, null,
            Array.isArray(toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment)
                ? toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment.map(function (i, idx) { return (React.createElement(React.Fragment, { key: idx }, i)); }) : toolbar === null || toolbar === void 0 ? void 0 : toolbar.fragment,
            contextMenu.element,
            fragment), tree: React.createElement(TreeView, __assign({ nodeData: getNodeData(), loading: internalLoading, disabled: disabled, error: error, checkableConfig: {
                checkableNode: checkableConfig === null || checkableConfig === void 0 ? void 0 : checkableConfig.checkableNode,
                checkableHighlighting: checkableConfig === null || checkableConfig === void 0 ? void 0 : checkableConfig.checkableHighlighting,
                activateNodeOnCheck: checkableConfig === null || checkableConfig === void 0 ? void 0 : checkableConfig.activateNodeOnCheck
            } }, checkableConfig, expandConfig, texts, restConfig, { selected: value.selection, onSelect: onSelectCallback, checked: value.checked, onCheck: onCheckCallback, expanded: value.expanded, onExpand: onExpandCallback, onToggleExpandNode: onToggleExpandNodeCallback, onContextMenu: onConxtextMenuCallback, isNodeLoading: function (node) {
                return loadingIds.includes(node.id);
            }, nodeWarningGetter: function (node) {
                var _a, _b, _c;
                var nodeWarningError = (_a = warningMap.find(function (warning) { return warning[0] === node.id; })) === null || _a === void 0 ? void 0 : _a[1];
                if (nodeWarningError) {
                    return ((_c = (_b = config.nodeWarningGetter) === null || _b === void 0 ? void 0 : _b.call(config, node, nodeWarningError)) !== null && _c !== void 0 ? _c : (nodeWarningError.message || 'Node loading failed'));
                }
                return;
            }, isNodeDisabled: config.isNodeDisabled })) }));
}
//# sourceMappingURL=DataTree.js.map